import { useEffect, useRef } from 'react';
import { useAppStore, runKeyframeInterpolation } from '../store/useAppStore';

/**
 * Drives the playback RAF loop and auto-scrolls the track area to keep the
 * playhead visible. Both effects read live store state at tick-time so they
 * never go stale.
 */
export function usePlayback(
  trackScrollRef: React.RefObject<HTMLDivElement | null>,
  pxPerFrame: number,
): void {
  const isPlaying    = useAppStore((s) => s.isPlaying);
  const currentFrame = useAppStore((s) => s.currentFrame);

  const rafRef     = useRef<number | null>(null);
  const lastTsRef  = useRef<number | null>(null);
  const playDirRef = useRef<1 | -1>(1);

  // ── Playback RAF loop ─────────────────────────────────────────────────────
  useEffect(() => {
    if (!isPlaying) {
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
      lastTsRef.current = null;
      return;
    }
    playDirRef.current = 1;
    const tick = (ts: number) => {
      if (!lastTsRef.current) lastTsRef.current = ts;
      const elapsed = ts - lastTsRef.current;
      lastTsRef.current = ts;
      const s = useAppStore.getState();
      const startF  = s.inPoint ?? 0;
      const endF    = Math.min(s.outPoint ?? (s.totalFrames - 1), s.totalFrames - 1);
      const safeEnd = Math.max(startF + 0.001, endF);
      const dir  = playDirRef.current;
      const next = s.currentFrame + dir * (elapsed / (1000 / s.fps));
      if (s.playbackMode === 'once') {
        if (next >= safeEnd) {
          useAppStore.setState({ currentFrame: safeEnd, isPlaying: false });
          runKeyframeInterpolation();
          return;
        }
        useAppStore.setState({ currentFrame: Math.max(startF, next) });
        runKeyframeInterpolation();
      } else if (s.playbackMode === 'loop') {
        const range = safeEnd - startF;
        let pos = next;
        if (range > 0.001) {
          pos = startF + ((pos - startF) % range);
          if (pos < startF) pos += range;
        } else {
          pos = startF;
        }
        useAppStore.setState({ currentFrame: pos });
        runKeyframeInterpolation();
      } else {
        // pingpong
        let pos = next;
        const range = safeEnd - startF;
        if (range > 0.001) {
          if (pos > safeEnd) {
            playDirRef.current = -1;
            pos = safeEnd - Math.min(Math.abs(pos - safeEnd), range);
          } else if (pos < startF) {
            playDirRef.current = 1;
            pos = startF + Math.min(Math.abs(pos - startF), range);
          }
        } else {
          pos = startF;
        }
        useAppStore.setState({ currentFrame: pos });
        runKeyframeInterpolation();
      }
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => { if (rafRef.current !== null) cancelAnimationFrame(rafRef.current); };
  }, [isPlaying]);

  // ── Auto-scroll playhead into view during playback ────────────────────────
  useEffect(() => {
    if (!isPlaying) return;
    const el = trackScrollRef.current;
    if (!el) return;
    const hx = Math.round(currentFrame) * pxPerFrame;
    const sl = el.scrollLeft;
    const w  = el.clientWidth;
    if (hx < sl + 20 || hx > sl + w - 40) {
      el.scrollLeft = Math.max(0, hx - w * 0.15);
    }
  }, [currentFrame, isPlaying, pxPerFrame, trackScrollRef]);
}
